<template>
  <dn-error-boundary>
    <div class="dn-carousel">
      <Flicking
        :key="route.fullPath"
        :options="{
          align: align,
          circular: circular,
          panelsPerView: panelsPerViewRef,
          firstPanelSize: firstPanelSize,
          hideBeforeInit: false,
          horizontal: true,
          bound: false,
          defaultIndex: 0,
          renderOnlyVisible: false,
          adaptive: adaptive,
          preventDefaultOnDrag: true,
          animation: {
            deceleration: 0.0075
          },
          input: {
            moveType: 'snap',
            bounce: '100%'
          }
        }"
        :plugins="plugins"
      >
        <slot />
        <template #viewport>
          <div
            v-if="shouldShowPagination"
            class="flicking-pagination"
          />
          <span
            v-if="shouldShowArrow"
            class="flicking-arrow-prev is-thin is-circle"
          />
          <span
            v-if="shouldShowArrow"
            class="flicking-arrow-next is-thin is-circle"
          />
        </template>
      </Flicking>
    </div>
  </dn-error-boundary>
</template>

<script setup lang="ts">
import Flicking from '@egjs/vue3-flicking';
import { Arrow, AutoPlay, Pagination } from '@egjs/flicking-plugins';
import debounce from 'lodash.debounce';
import { useRoute } from 'vue-router';

const props = defineProps({
  firstPanelSize: {
    type: String,
    required: false,
    default: '300px'
  },
  panelsPerView: {
    type: String,
    required: false,
    default: '-1'
  },
  breakpoints: {
    type: Object,
    required: false,
    default: null
  },
  align: {
    type: String,
    required: false,
    default: 'center'
  },
  circular: {
    type: Boolean,
    required: false,
    default: false
  },
  adaptive: {
    type: Boolean,
    required: false,
    default: true
  },
  shouldShowArrow: {
    type: Boolean,
    required: false,
    default: true
  },
  shouldShowPagination: {
    type: Boolean,
    required: false,
    default: false
  },
  arrowMoveCount: {
    type: Number,
    required: false,
    default: 1
  },
  paginationType: {
    type: String as () => 'bullet' | 'fraction' | 'scroll',
    required: false,
    default: 'scroll'
  },
  shouldAutoPlay: {
    type: Boolean,
    required: false,
    default: false
  },
  autoPlayDuration: {
    type: Number,
    required: false,
    default: 2000
  },
  stopOnHover: {
    type: Boolean,
    required: false,
    default: true
  }
});

const route = useRoute();
const panelsPerViewRef = ref(props.panelsPerView);
const breakpointKeys = props.breakpoints
  ? Object.keys(props.breakpoints)
    .map(Number)
    .sort((a, b) => b - a)
  : null;

const plugins: any[] = [];
if (props.shouldShowPagination) {
  plugins.push(new Pagination({ type: props.paginationType }));
}
if (props.shouldShowArrow) {
  plugins.push(new Arrow({ moveCount: props.arrowMoveCount }));
}
if (props.shouldAutoPlay) {
  plugins.push(
    new AutoPlay({
      duration: props.autoPlayDuration,
      stopOnHover: props.stopOnHover
    })
  );
}

function setCorrectBreakpointConfig () {
  if (!breakpointKeys) {
    return;
  }
  const innerWidth = window.innerWidth;
  let breakpoint = null;
  for (let i = 0; i < breakpointKeys.length; i++) {
    if (innerWidth >= breakpointKeys[i]) {
      breakpoint = props.breakpoints[breakpointKeys[i]];
      break;
    }
  }
  if (!breakpoint) {
    breakpoint = props.breakpoints[breakpointKeys[0]];
  }
  panelsPerViewRef.value = breakpoint.panelsPerView;
}

const debounceSetConfig = debounce(setCorrectBreakpointConfig, 500);
onMounted(() => {
  if (props.breakpoints) {
    setCorrectBreakpointConfig();
    useEventListener('resize', debounceSetConfig);
  }
});
</script>

<style lang="scss">
@import '../../node_modules/@egjs/vue3-flicking/dist/flicking.css';
@import '../../node_modules/@egjs/flicking-plugins/dist/pagination.css';

.dn-carousel {
  position: relative;
  margin-block-end: var(--row-gap);
  margin: 0 auto;
  max-width: var(--dn-collection-content-width, 980px);
}

.flicking-viewport {
  position: relative;
  width: 100%;
  transition: height 500ms;
  max-width: var(--dn-collection-content-width, 980px);
  gap: 20px;

  .flicking-camera {
    .dn-job-item {
      width: 250.66px;

      @media only screen and (width >= 1024px) {
        width: 215px;
      }

      @media only screen and (width >= 768px) {
        width: 235.667px;
      }
    }

    .dn-link,
    .dn-image {
      margin-right: var(--row-gap);
    }
  }
}

.flicking-arrow-prev,
.flicking-arrow-next {
  position: absolute;
  top: 50%;
  width: 40px;
  height: 40px;
  cursor: pointer;
  transform: translateY(-50%);
  z-index: 2;

  &.is-circle {
    background-color: var(--color-brand-utility-50);
    border-radius: 50%;

    &.flicking-arrow-disabled {
      background-color: rgb(10 10 10 / 10%);
      display: none;
    }

    &::before,
    &::after {
      background-color: var(--color-brand-utility-900);
    }
  }

  &.is-outside {
    &.flicking-arrow-prev {
      left: -74px;
    }

    &.flicking-arrow-next {
      right: -74px;
    }
  }

  &::before,
  &::after {
    content: '';
    width: 14px;
    height: 4px;
    position: absolute;
    background-color: var(--color-link-default);
  }

  &.flicking-arrow-prev {
    left: 10px;

    &::before {
      top: 50%;
      left: 15px;
      transform: rotate(-45deg);
      transform-origin: 0 50%;
    }

    &::after {
      top: calc(50% - 4px);
      left: 15px;
      transform: rotate(45deg);
      transform-origin: 0 50%;
    }
  }

  &.flicking-arrow-next {
    right: 10px;

    &::before {
      top: 50%;
      right: 15px;
      transform: rotate(45deg);
      transform-origin: 100% 50%;
    }

    &::after {
      top: calc(50% - 4px);
      right: 15px;
      transform: rotate(-45deg);
      transform-origin: 100% 50%;
    }
  }

  &.flicking-arrow-disabled {
    cursor: default;
    transition: all 0.3s ease;

    &::before,
    &::after {
      background-color: #e6e6e6;
    }
  }

  &.is-thin {
    &::before,
    &::after {
      height: 3px;
    }

    &::after {
      top: calc(50% - 2px);
    }
  }
}

.flicking-pagination {
  position: relative;
  left: 0;
  margin-top: 30px;
  width: 100%;
  text-align: center;
  z-index: 2;

  &-bullet {
    display: inline-block;
    width: 10px;
    height: 10px;
    margin: 0 5px;
    border-radius: 50%;
    background-color: var(--color-ui-generic-fg);
    cursor: pointer;
    font-size: 1rem;
    opacity: 0.2;

    &-active {
      opacity: 1;
    }

    &-prev,
    &-next {
      transform: scale(0.66);
    }

    &-prev2,
    &-next2 {
      transform: scale(0.33);
    }

    &-scroll {
      vertical-align: middle;
      position: relative;
      transition: 0.2s transform;

      &-active {
        transform: scale(1);
      }
    }
  }

  &-bullets,
  &-scroll {
    font-size: 0;
  }

  &-scroll {
    left: 50%;
    transform: translate(-50%);
    white-space: nowrap;
    overflow: hidden;

    .flicking-pagination-slider {
      transition: 0.2s transform;
    }

    &.flicking-pagination-uninitialized {
      .flicking-pagination-slider,
      .flicking-pagination-bullet {
        transition: none;
      }
    }
  }
}
</style>
